// User reports + banned-accounts server functions.
// Tables (banned_accounts, user_reports, ban_violation_category, user_report_*)
// are not yet in the generated Supabase types — we use `as never` casts.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const REPORT_REASONS = [
  { value: "suspected_counterfeit", label: "Suspected counterfeit card" },
  { value: "item_not_received", label: "Item not received" },
  { value: "item_not_as_described", label: "Item significantly not as described" },
  { value: "suspected_ban_evasion", label: "Suspected ban evasion" },
  { value: "harassment", label: "Harassment or threats" },
  { value: "fake_photos", label: "Fake or manipulated photos" },
] as const;
export type ReportReasonValue = (typeof REPORT_REASONS)[number]["value"];

export const BAN_CATEGORIES: Record<string, string> = {
  confirmed_fraud: "Confirmed Fraud",
  counterfeit_cards: "Counterfeit Cards",
  ban_evasion: "Ban Evasion",
  harassment: "Harassment",
  fee_avoidance: "Fee Avoidance",
  platform_policy_violation: "Platform Policy Violation",
};

export type BannedAccountRow = {
  id: string;
  username: string;
  violation_category: string;
  banned_at: string;
};

// -------------------------------------------------------------------
// Public: list banned accounts (no auth).
// -------------------------------------------------------------------
export const listBannedAccounts = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ entries: BannedAccountRow[] }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await (supabaseAdmin as never as {
      from: (t: string) => {
        select: (s: string) => { order: (k: string, o: { ascending: boolean }) => Promise<{ data: BannedAccountRow[] | null; error: { message: string } | null }> };
      };
    })
      .from("banned_accounts")
      .select("id, username, violation_category, banned_at")
      .order("banned_at", { ascending: false });
    if (error) throw new Error(error.message);
    return { entries: data ?? [] };
  },
);

// -------------------------------------------------------------------
// Submit a user report.
// -------------------------------------------------------------------
const submitInput = z.object({
  reported_username: z.string().trim().min(1).max(40),
  reason: z.enum([
    "suspected_counterfeit",
    "item_not_received",
    "item_not_as_described",
    "suspected_ban_evasion",
    "harassment",
    "fake_photos",
  ]),
  description: z.string().trim().min(50, "Please describe the issue in at least 50 characters").max(2000),
  order_id: z.string().trim().max(120).optional().or(z.literal("").transform(() => undefined)),
  context_type: z.enum(["profile", "offer", "message", "order"]).default("profile"),
  context_id: z.string().trim().max(120).optional().or(z.literal("").transform(() => undefined)),
  good_faith: z.literal(true, { message: "Please confirm good-faith acknowledgement" }),
});

export const submitUserReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => submitInput.parse(i))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Look up reported profile
    const { data: target } = await supabaseAdmin
      .from("profiles")
      .select("id, username")
      .eq("username", data.reported_username.toLowerCase())
      .maybeSingle();
    if (!target) throw new Error("We couldn't find a user with that username.");
    if (target.id === context.userId) throw new Error("You can't report yourself.");

    // Reporter profile state — privilege & age checks
    const { data: reporter } = await supabaseAdmin
      .from("profiles")
      .select("id, created_at, reporting_suspended")
      .eq("id", context.userId)
      .maybeSingle<{ id: string; created_at: string; reporting_suspended: boolean }>();
    if (!reporter) throw new Error("Your profile is not ready yet — please complete onboarding first.");
    if (reporter.reporting_suspended) {
      throw new Error(
        "Your reporting privileges have been suspended due to repeated false reports. Contact support if you believe this is a mistake.",
      );
    }
    const ageMs = Date.now() - new Date(reporter.created_at).getTime();
    if (ageMs < 7 * 24 * 60 * 60 * 1000) {
      throw new Error(
        "Accounts must be at least 7 days old to submit reports. Please come back once your account is older.",
      );
    }

    // Rate-limit: one report per reported user per 24h
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const { data: recent } = await (supabaseAdmin as never as {
      from: (t: string) => {
        select: (s: string) => { eq: (k: string, v: string) => { eq: (k: string, v: string) => { gte: (k: string, v: string) => { limit: (n: number) => Promise<{ data: unknown[] | null }> } } } };
      };
    })
      .from("user_reports")
      .select("id")
      .eq("reporter_id", context.userId)
      .eq("reported_user_id", target.id)
      .gte("created_at", since)
      .limit(1);
    if ((recent ?? []).length > 0) {
      throw new Error("You already submitted a report against this user in the last 24 hours.");
    }

    const { error } = await (supabaseAdmin as never as { from: (t: string) => { insert: (row: Record<string, unknown>) => Promise<{ error: { message: string } | null }> } })
      .from("user_reports")
      .insert({
        reporter_id: context.userId,
        reported_user_id: target.id,
        reason: data.reason,
        description: data.description,
        order_id: data.order_id ?? null,
        context_type: data.context_type,
        context_id: data.context_id ?? null,
      });
    if (error) throw new Error(error.message);

    return { ok: true };
  });

// -------------------------------------------------------------------
// Admin: list reports (sorted, priority first)
// -------------------------------------------------------------------
async function assertAdmin(supabase: { rpc: (fn: "has_role", a: { _user_id: string; _role: "admin" }) => PromiseLike<{ data: boolean | null }> }, userId: string) {
  const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (!data) throw new Error("Forbidden");
}

export type AdminReportRow = {
  id: string;
  reporter_username: string;
  reported_username: string;
  reason: string;
  description: string;
  order_id: string | null;
  context_type: string;
  status: string;
  created_at: string;
};

export const listAdminUserReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ reports: AdminReportRow[] }> => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await (supabaseAdmin as never as {
      from: (t: string) => {
        select: (s: string) => { order: (k: string, o: { ascending: boolean }) => { limit: (n: number) => Promise<{ data: Array<{ id: string; reporter_id: string; reported_user_id: string; reason: string; description: string; order_id: string | null; context_type: string; status: string; created_at: string }> | null; error: { message: string } | null }> } };
      };
    })
      .from("user_reports")
      .select("id, reporter_id, reported_user_id, reason, description, order_id, context_type, status, created_at")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    const ids = Array.from(new Set(rows.flatMap((r) => [r.reporter_id, r.reported_user_id])));
    const { data: profiles } = await supabaseAdmin.from("profiles").select("id, username").in("id", ids);
    const map = new Map((profiles ?? []).map((p) => [p.id, p.username]));

    // Priority order: ban_evasion + counterfeit first
    const priority = (r: { reason: string; status: string }) => {
      let p = 0;
      if (r.status === "pending") p += 100;
      if (r.status === "under_investigation") p += 50;
      if (r.reason === "suspected_ban_evasion") p += 30;
      if (r.reason === "suspected_counterfeit") p += 20;
      return p;
    };
    const enriched = rows
      .map((r) => ({
        id: r.id,
        reporter_username: map.get(r.reporter_id) ?? r.reporter_id.slice(0, 8),
        reported_username: map.get(r.reported_user_id) ?? r.reported_user_id.slice(0, 8),
        reason: r.reason,
        description: r.description,
        order_id: r.order_id,
        context_type: r.context_type,
        status: r.status,
        created_at: r.created_at,
      }))
      .sort((a, b) => priority(b) - priority(a) || (b.created_at > a.created_at ? 1 : -1));
    return { reports: enriched };
  });

// -------------------------------------------------------------------
// Admin: update report status
// -------------------------------------------------------------------
const updateInput = z.object({
  report_id: z.string().uuid(),
  status: z.enum(["under_investigation", "confirmed_action_taken", "dismissed_false_report"]),
});

export const updateUserReportStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => updateInput.parse(i))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row, error } = await (supabaseAdmin as never as {
      from: (t: string) => {
        update: (u: Record<string, unknown>) => {
          eq: (k: string, v: string) => {
            select: (s: string) => { maybeSingle: () => Promise<{ data: { id: string; reporter_id: string } | null; error: { message: string } | null }> };
          };
        };
      };
    })
      .from("user_reports")
      .update({ status: data.status, reviewed_at: new Date().toISOString(), reviewed_by: context.userId })
      .eq("id", data.report_id)
      .select("id, reporter_id")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Report not found");

    // Notify reporter (generic, never discloses outcome detail)
    if (data.status === "confirmed_action_taken") {
      await supabaseAdmin.from("notifications").insert({
        user_id: row.reporter_id,
        type: "system",
        title: "Your report was reviewed",
        body: "Your report has been reviewed and action was taken.",
      });
    } else if (data.status === "dismissed_false_report") {
      await supabaseAdmin.from("notifications").insert({
        user_id: row.reporter_id,
        type: "system",
        title: "Your report was reviewed",
        body: "Your report was reviewed and no violation was found.",
      });

      // Increment false_report_count via admin client (bypasses self-update trigger)
      const { data: reporter } = await supabaseAdmin
        .from("profiles")
        .select("false_report_count, reporting_suspended")
        .eq("id", row.reporter_id)
        .maybeSingle<{ false_report_count: number; reporting_suspended: boolean }>();
      const nextCount = (reporter?.false_report_count ?? 0) + 1;
      const suspend = nextCount >= 3;
      await supabaseAdmin
        .from("profiles")
        .update({
          false_report_count: nextCount,
          reporting_suspended: suspend ? true : (reporter?.reporting_suspended ?? false),
        } as never)
        .eq("id", row.reporter_id);
      if (suspend && !reporter?.reporting_suspended) {
        await supabaseAdmin.from("notifications").insert({
          user_id: row.reporter_id,
          type: "system",
          title: "Reporting privileges suspended",
          body: "Your reporting privileges have been suspended after 3 false reports. You may contact support if you believe this was applied in error.",
        });
      }
    }

    return { ok: true };
  });
